import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  HStack,
  VStack,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  Tooltip,
  Badge,
  Button
} from '@chakra-ui/react';
import {
  Play,
  Pause,
  ArrowsOut,
  Download,
  House,
  ArrowCounterClockwise,
  Eye,
  EyeSlash,
  MagnifyingGlassPlus,
  MagnifyingGlassMinus,
  ArrowsOutCardinal,
  Info,
  ChartLine,
  NavigationArrow
} from '@phosphor-icons/react';
import * as THREE from 'three';
import ThreeDAnalysisTools from './media/ThreeDAnalysisTools';
import ThreeDCameraControls from './media/ThreeDCameraControls';

// We'll need to dynamically import these loaders since they're not in the main Three.js bundle
const loadThreeLoaders = async () => {
  const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
  const { OBJLoader } = await import('three/examples/jsm/loaders/OBJLoader.js');
  const { STLLoader } = await import('three/examples/jsm/loaders/STLLoader.js');
  const { PLYLoader } = await import('three/examples/jsm/loaders/PLYLoader.js');
  const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js');
  
  return { GLTFLoader, OBJLoader, STLLoader, PLYLoader, OrbitControls };
};

const ThreeDViewer = ({
  url,
  fileName,
  fileExtension,
  width = '100%',
  height = '400px',
  showControls = true,
  autoRotate = false,
  onFullscreen,
  onDownload
}) => {
  // console.log('[ThreeDViewer] Props received:', { url, fileName, fileExtension, width, height, showControls, autoRotate }); // Temporarily removed for cleaner logs
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const modelRef = useRef(null);
  const animationRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progressPercent, setProgressPercent] = useState(0); // Added for progress tracking
  const [isRotating, setIsRotating] = useState(autoRotate);
  const isRotatingRef = useRef(isRotating); // Ref to hold the latest isRotating value for animate loop
  const [wireframe, setWireframe] = useState(false);
  const [modelInfo, setModelInfo] = useState(null);
  const [showAnalysisTools, setShowAnalysisTools] = useState(false);
  const [showCameraControls, setShowCameraControls] = useState(false);
  const [cameraRef, setCameraRef] = useState(null);

  // Theme colors
  const bgColor = useColorModeValue('bg.surface', 'bg.surface');
  const borderColor = useColorModeValue('border.default', 'border.default');
  const controlsBg = useColorModeValue('bg.accent', 'bg.accent');
  const errorBg = useColorModeValue('error', 'error');

  // Get file extension from filename or URL
  const getFileExtension = () => {
    if (fileExtension) return fileExtension.toLowerCase();
    if (fileName) return fileName.split('.').pop().toLowerCase();
    return url.split('.').pop().split('?')[0].toLowerCase();
  };

  // Initialize Three.js scene
  const initScene = () => {
    if (!mountRef.current) return;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf0f0f0); // Default background, can be customized via props if needed
    sceneRef.current = scene;
    console.log('[ThreeDViewer] Scene initialized. Background color set to 0xf0f0f0.');

    // Camera
    const camera = new THREE.PerspectiveCamera(
      75, // FOV
      mountRef.current.clientWidth / mountRef.current.clientHeight, // Aspect ratio
      0.1, // Near clipping plane
      1000 // Far clipping plane
    );
    camera.position.set(5, 5, 5); // Default camera position
    console.log('[ThreeDViewer] Camera initialized. Position:', camera.position.toArray().join(', '));

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    renderer.shadowMap.enabled = true; // Enable shadows
    renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Softer shadows
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    console.log('[ThreeDViewer] Renderer initialized and appended. Size:', mountRef.current.clientWidth, 'x', mountRef.current.clientHeight);

    // Lighting
    const ambientLight = new THREE.AmbientLight(0x404040, 0.6); // Soft white light
    scene.add(ambientLight);
    console.log('[ThreeDViewer] Ambient light added. Intensity: 0.6');

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(10, 10, 5); // Position the light
    directionalLight.castShadow = true; // Allow shadow casting
    scene.add(directionalLight);
    console.log('[ThreeDViewer] Directional light added. Intensity: 0.8, Position:', directionalLight.position.toArray().join(', '));

    const pointLight = new THREE.PointLight(0xffffff, 0.5);
    pointLight.position.set(-10, -10, -5);
    scene.add(pointLight);
    console.log('[ThreeDViewer] Point light added. Intensity: 0.5, Position:', pointLight.position.toArray().join(', '));
    
    return { scene, camera, renderer };
  };

  // Load 3D model based on file extension
  const loadModel = async (scene, camera, renderer) => {
    console.log('[ThreeDViewer] loadModel called. URL:', url);
    setLoading(true);
    setError(null);
    try {
      const extension = getFileExtension();
      console.log('[ThreeDViewer] Determined file extension:', extension);

      console.log('[ThreeDViewer] Attempting to load Three.js loaders dynamically...');
      const loaders = await loadThreeLoaders();
      console.log('[ThreeDViewer] Three.js loaders loaded:', loaders);
      
      let loader;
      // Default material for formats that don't include materials (OBJ, STL, PLY)
      let material = new THREE.MeshLambertMaterial({
        color: 0x8B4513, // A brown color, good for untextured models
        side: THREE.DoubleSide // Render both sides of faces
      });
      console.log('[ThreeDViewer] Default material created for non-GLTF formats.');

      switch (extension) {
        case 'gltf':
        case 'glb':
          loader = new loaders.GLTFLoader();
          console.log('[ThreeDViewer] GLTFLoader instantiated for', extension);
          break;
        case 'obj':
          loader = new loaders.OBJLoader();
          console.log('[ThreeDViewer] OBJLoader instantiated.');
          break;
        case 'stl':
          loader = new loaders.STLLoader();
          console.log('[ThreeDViewer] STLLoader instantiated.');
          break;
        case 'ply':
          loader = new loaders.PLYLoader();
          console.log('[ThreeDViewer] PLYLoader instantiated.');
          break;
        default:
          console.error(`[ThreeDViewer] Unsupported file format: ${extension}`);
          throw new Error(`Unsupported file format: ${extension}`);
      }

      console.log(`[ThreeDViewer] Attempting to load model from URL: ${url} using ${loader.constructor.name}`);
      loader.load(
        url,
        // onSuccess callback
        (result) => {
          console.log('[ThreeDViewer] Model loaded successfully by loader. Raw result:', result);
          let model;
          
          if (extension === 'gltf' || extension === 'glb') {
            model = result.scene; // GLTF/GLB result has a 'scene' property
            console.log('[ThreeDViewer] Using result.scene for GLTF/GLB model.');
          } else if (extension === 'obj') {
            model = result; // OBJLoader returns the group directly
            console.log('[ThreeDViewer] Using result directly for OBJ model. Applying default material...');
            model.traverse((child) => {
              if (child.isMesh) {
                child.material = material;
                child.castShadow = true;
                child.receiveShadow = true;
              }
            });
          } else {
            // For STL and PLY, the result is a BufferGeometry
            const geometry = result;
            console.log('[ThreeDViewer] Using result as geometry for STL/PLY. Creating mesh with default material...');
            geometry.computeVertexNormals(); // Important for lighting
            model = new THREE.Mesh(geometry, material);
            model.castShadow = true;
            model.receiveShadow = true;
          }

          console.log('[ThreeDViewer] Processing loaded model:', model);
          // Center and scale the model
          const box = new THREE.Box3().setFromObject(model);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          console.log('[ThreeDViewer] Model BoundingBox:', box, 'Center:', center, 'Size:', size);
          
          const maxDimension = Math.max(size.x, size.y, size.z);
          // Avoid division by zero or very small numbers if model is flat or tiny
          const scale = (maxDimension > 0.001) ? (4 / maxDimension) : 1;
          console.log('[ThreeDViewer] Calculated maxDimension:', maxDimension, 'Scale:', scale);
          
          model.scale.setScalar(scale);
          model.position.sub(center.multiplyScalar(scale)); // Center model at origin after scaling
          console.log('[ThreeDViewer] Model scaled and centered. New scale:', model.scale.toArray().join(', '), 'New position:', model.position.toArray().join(', '));

          scene.add(model);
          modelRef.current = model;
          console.log('[ThreeDViewer] Model added to scene.');

          // Set model info for display
          const newModelInfo = {
            vertices: getVertexCount(model),
            faces: getFaceCount(model),
            size: size, // Original size before scaling
            boundingBox: box // Original bounding box
          };
          setModelInfo(newModelInfo);
          console.log('[ThreeDViewer] Model info set:', newModelInfo);

          // Setup OrbitControls
          console.log('[ThreeDViewer] Setting up OrbitControls...');
          const controls = new loaders.OrbitControls(camera, renderer.domElement);
          controls.enableDamping = true; // Smooths camera movement
          controls.dampingFactor = 0.05;
          // Enable mouse/touch controls
          controls.enableZoom = true;
          controls.enablePan = true;
          controls.enableRotate = true;
          // Configure control sensitivity
          controls.zoomSpeed = 1.0;
          controls.panSpeed = 0.8;
          controls.rotateSpeed = 1.0;
          // Set zoom limits
          controls.minDistance = 1;
          controls.maxDistance = 100;
          // Set rotation limits (optional)
          controls.minPolarAngle = 0; // radians
          controls.maxPolarAngle = Math.PI; // radians
          // Explicitly set autoRotate based on the initial isRotating state, which comes from the autoRotate prop.
          controls.autoRotate = isRotating;
          controls.autoRotateSpeed = 2.0;
          controlsRef.current = controls;
          console.log(`[ThreeDViewer] OrbitControls setup complete. Initial controls.autoRotate: ${controls.autoRotate}, isRotating state: ${isRotating}`);

          // Start animation loop after controls are set up and model is loaded
          // A small delay can sometimes help ensure everything is ready.
          setTimeout(() => {
            setLoading(false);
            console.log('[ThreeDViewer] Loading complete. Starting animation loop.');
          }, 100);
        },
        // onProgress callback
        (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const percentLoaded = (progressEvent.loaded / progressEvent.total) * 100;
            setProgressPercent(percentLoaded); // Update state for UI
            // console.log(`[ThreeDViewer] Model loading progress: ${percentLoaded.toFixed(2)}%`); // Keep console log if needed for detailed debugging
          } else {
            // If total size is unknown, we can't calculate percentage,
            // but we can show that data is being received.
            // For simplicity, we'll just log and not update a visual for this case.
            console.log(`[ThreeDViewer] Model loading progress: ${progressEvent.loaded} bytes loaded (total unknown)`);
          }
        },
        // onError callback
        (loadError) => {
          console.error('[ThreeDViewer] Error during loader.load:', loadError);
          setError(`Failed to load 3D model: ${loadError.message || 'Unknown loading error'}`);
          setLoading(false);
        }
      );
    } catch (err) {
      console.error('[ThreeDViewer] Error in loadModel (e.g., unsupported format, loader init failed):', err);
      setError(`Error setting up loader or unsupported file: ${err.message}`);
      setLoading(false);
    }
  };

  // Helper functions for model info
  const getVertexCount = (model) => {
    let count = 0;
    model.traverse((child) => {
      if (child.isMesh && child.geometry) {
        count += child.geometry.attributes.position?.count || 0;
      }
    });
    return count;
  };

  const getFaceCount = (model) => {
    let count = 0;
    model.traverse((child) => {
      if (child.isMesh && child.geometry) {
        if (child.geometry.index) {
          count += child.geometry.index.count / 3;
        } else {
          count += (child.geometry.attributes.position?.count || 0) / 3;
        }
      }
    });
    return Math.floor(count);
  };

  // Animation loop
  let frameCount = 0; // For throttled logging
  const animate = () => {
    animationRef.current = requestAnimationFrame(animate); // Request next frame first

    if (!rendererRef.current || !sceneRef.current || !controlsRef.current || !controlsRef.current.object) {
      return;
    }

    if (controlsRef.current) {
      // Use the ref for the most up-to-date value in the animation loop
      if (controlsRef.current.autoRotate !== isRotatingRef.current) {
        controlsRef.current.autoRotate = isRotatingRef.current;
        // console.log(`[ThreeDViewer] Animate: Synced controls.autoRotate to isRotatingRef.current: ${isRotatingRef.current}`);
      }
      controlsRef.current.update();
    }

    // Throttled logging for rotation state in animate loop
    // frameCount++;
    // if (frameCount % 120 === 0) { // Log every ~2 seconds (assuming 60fps)
    //   console.log(`[ThreeDViewer] Animate loop: isRotating state: ${isRotating}, controls.autoRotate: ${controlsRef.current?.autoRotate}`);
    // }

    try {
      rendererRef.current.render(sceneRef.current, controlsRef.current.object);
    } catch (renderError) {
      console.error('[ThreeDViewer] Error during scene render:', renderError);
      cancelAnimationFrame(animationRef.current); // Stop animation on render error
      setError('A rendering error occurred. Please try reloading the model.');
    }
  };

  // Handle window resize
  const handleResize = () => {
    if (!mountRef.current || !rendererRef.current || !controlsRef.current) return;

    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;

    controlsRef.current.object.aspect = width / height;
    controlsRef.current.object.updateProjectionMatrix();
    rendererRef.current.setSize(width, height);
  };

  // Toggle wireframe mode
  const toggleWireframe = () => {
    console.log('[ThreeDViewer] toggleWireframe called. Current wireframe state (before toggle):', wireframe);
    if (!modelRef.current) {
      console.warn('[ThreeDViewer] toggleWireframe: modelRef.current is null. Cannot change wireframe.');
      return;
    }
    
    const newWireframeState = !wireframe;
    const processedMaterials = new Set(); // Track unique materials to avoid duplicates
    let materialCount = 0;
    let meshCount = 0;

    try {
      modelRef.current.traverse((child) => {
        if (child.isMesh && child.material) {
          meshCount++;
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          
          materials.forEach((mat) => {
            if (!processedMaterials.has(mat.uuid)) {
              processedMaterials.add(mat.uuid);
              
              // Store original material properties for restoration
              if (!mat.userData.originalWireframe) {
                mat.userData.originalWireframe = mat.wireframe;
              }
              
              // Set wireframe and force update
              mat.wireframe = newWireframeState;
              mat.needsUpdate = true;
              
              // Force material to update its shader
              if (mat.dispose) {
                mat.dispose();
              }
              
              materialCount++;
              console.log(`[ThreeDViewer] Updated material ${mat.name || mat.uuid} (type: ${mat.type}) wireframe to:`, newWireframeState);
            }
          });
          
          // Force mesh to update
          child.material.needsUpdate = true;
        }
      });

      // Force a render update
      if (rendererRef.current && sceneRef.current && controlsRef.current?.object) {
        rendererRef.current.render(sceneRef.current, controlsRef.current.object);
      }

      setWireframe(newWireframeState);
      console.log(`[ThreeDViewer] Wireframe toggle complete. Updated ${materialCount} unique materials across ${meshCount} meshes. New state:`, newWireframeState);
    } catch (error) {
      console.error('[ThreeDViewer] Error during wireframe toggle:', error);
      // Reset state if there was an error
      setWireframe(wireframe);
    }
  };

  // Zoom functions
  const zoomIn = () => {
    if (!controlsRef.current) return;
    const camera = controlsRef.current.object;
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);
    camera.position.add(direction.multiplyScalar(0.5));
    controlsRef.current.update();
  };

  const zoomOut = () => {
    if (!controlsRef.current) return;
    const camera = controlsRef.current.object;
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);
    camera.position.add(direction.multiplyScalar(-0.5));
    controlsRef.current.update();
  };

  // Reset camera position
  const resetCamera = () => {
    if (!controlsRef.current) return;
    
    controlsRef.current.object.position.set(5, 5, 5);
    controlsRef.current.target.set(0, 0, 0);
    controlsRef.current.update();
  };

  // Initialize on mount
  useEffect(() => {
    if (!url) return;

    const sceneData = initScene();
    if (sceneData) {
      loadModel(sceneData.scene, sceneData.camera, sceneData.renderer);
      // Start animation after a small delay to ensure everything is set up
      setTimeout(() => {
        animate();
      }, 100);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (mountRef.current && rendererRef.current) {
        mountRef.current.removeChild(rendererRef.current.domElement);
      }
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
    };
  }, [url]);

  // Update rotation state and ref when prop changes or on initial mount
  useEffect(() => {
    console.log(`[ThreeDViewer] useEffect for autoRotate prop. autoRotate: ${autoRotate}. Updating isRotating state and ref.`);
    setIsRotating(autoRotate);
    isRotatingRef.current = autoRotate; // Keep ref in sync with state from prop
    if (controlsRef.current) {
      // This ensures that if the prop changes, the controls are updated immediately
      // without waiting for the next animation frame's sync.
      controlsRef.current.autoRotate = autoRotate;
      console.log(`[ThreeDViewer] useEffect for autoRotate: Synced controls.autoRotate to ${autoRotate}`);
    }
  }, [autoRotate]);

  // Effect to keep isRotatingRef in sync with isRotating state when state changes (e.g., from button click)
  useEffect(() => {
    isRotatingRef.current = isRotating;
    // console.log(`[ThreeDViewer] isRotating state changed to: ${isRotating}. Updated isRotatingRef.current.`);
  }, [isRotating]);

  if (!url) {
    return (
      <Box 
        width={width} 
        height={height} 
        bg={bgColor} 
        border="1px" 
        borderColor={borderColor}
        borderRadius="md"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="text.muted">No 3D model URL provided</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={2} width={width}>
      <Box
        position="relative"
        width="100%"
        height={height}
        bg={bgColor}
        border="1px"
        borderColor={borderColor}
        borderRadius="md"
        overflow="hidden"
      >
        <Box ref={mountRef} width="100%" height="100%" />
        
        {loading && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            textAlign="center"
            p={4}
            bg={useColorModeValue("rgba(255,255,255,0.85)", "rgba(26,32,44,0.85)")} // Adjusted alpha for better visibility
            borderRadius="md"
            boxShadow="lg"
          >
            <Spinner size="xl" color="brand.primary" />
            <Text mt={3} color="text.emphasis" fontWeight="medium"> {/* Changed to text.emphasis */}
              Loading 3D model...
            </Text>
            {progressPercent > 0 && progressPercent < 100 && (
              <Text fontSize="sm" color="text.subtle" mt={1}>
                {progressPercent.toFixed(0)}%
              </Text>
            )}
             {progressPercent === 0 && (
              <Text fontSize="xs" color="text.subtle" mt={1}>
                (Preparing to load)
              </Text>
            )}
          </Box>
        )}

        {error && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            width="90%"
          >
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Loading Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Box>
            </Alert>
          </Box>
        )}

        {showControls && !loading && !error && (
          <Box
            position="absolute"
            top={2}
            right={2}
            bg={controlsBg}
            borderRadius="md"
            p={2}
          >
            <VStack spacing={1}>
              <HStack spacing={1}>
                <Tooltip label={isRotating ? "Stop rotation" : "Start rotation"}>
                  <IconButton
                    icon={isRotating ? <Pause /> : <Play />}
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const newRotationState = !isRotatingRef.current;
                      console.log(`[ThreeDViewer] Rotation button clicked. Current isRotatingRef.current: ${isRotatingRef.current}. Attempting to set state to: ${newRotationState}`);
                      setIsRotating(newRotationState);
                      if (controlsRef.current) {
                         controlsRef.current.autoRotate = newRotationState;
                         console.log(`[ThreeDViewer] Rotation button: Directly set controls.autoRotate to ${newRotationState}`);
                      }
                    }}
                  />
                </Tooltip>
                
                <Tooltip label={wireframe ? "Solid view" : "Wireframe view"}>
                  <IconButton
                    icon={wireframe ? <Eye /> : <EyeSlash />}
                    size="sm"
                    variant="ghost"
                    onClick={toggleWireframe}
                  />
                </Tooltip>
                
                <Tooltip label="Zoom in">
                  <IconButton
                    icon={<MagnifyingGlassPlus />}
                    size="sm"
                    variant="ghost"
                    onClick={zoomIn}
                  />
                </Tooltip>
                
                <Tooltip label="Zoom out">
                  <IconButton
                    icon={<MagnifyingGlassMinus />}
                    size="sm"
                    variant="ghost"
                    onClick={zoomOut}
                  />
                </Tooltip>
                
                <Tooltip label="Analysis tools">
                  <IconButton
                    icon={<ChartLine />}
                    size="sm"
                    variant={showAnalysisTools ? "solid" : "ghost"}
                    colorScheme={showAnalysisTools ? "blue" : "gray"}
                    onClick={() => setShowAnalysisTools(!showAnalysisTools)}
                  />
                </Tooltip>
                
                <Tooltip label="Camera controls">
                  <IconButton
                    icon={<NavigationArrow />}
                    size="sm"
                    variant={showCameraControls ? "solid" : "ghost"}
                    colorScheme={showCameraControls ? "blue" : "gray"}
                    onClick={() => setShowCameraControls(!showCameraControls)}
                  />
                </Tooltip>
              </HStack>
              
              <HStack spacing={1}>
                <Tooltip label="Reset camera">
                  <IconButton
                    icon={<House />}
                    size="sm"
                    variant="ghost"
                    onClick={resetCamera}
                  />
                </Tooltip>
                
                {onFullscreen && (
                  <Tooltip label="Fullscreen">
                    <IconButton
                      icon={<ArrowsOut />}
                      size="sm"
                      variant="ghost"
                      onClick={onFullscreen}
                    />
                  </Tooltip>
                )}
                
                {onDownload && (
                  <Tooltip label="Download">
                    <IconButton
                      icon={<Download />}
                      size="sm"
                      variant="ghost"
                      onClick={onDownload}
                    />
                  </Tooltip>
                )}
              </HStack>
            </VStack>
          </Box>
        )}
        
        {/* Analysis Tools */}
        {showAnalysisTools && !loading && !error && (
          <ThreeDAnalysisTools
            scene={sceneRef.current}
            camera={controlsRef.current?.object}
            renderer={rendererRef.current}
            controls={controlsRef.current}
            model={modelRef.current}
            isVisible={showAnalysisTools}
            onToggle={() => setShowAnalysisTools(false)}
            onMeasurement={(measurement) => {
              console.log('New measurement:', measurement);
            }}
            onAnnotation={(annotation) => {
              console.log('New annotation:', annotation);
            }}
          />
        )}
        
        {/* Camera Controls */}
        {showCameraControls && !loading && !error && (
          <ThreeDCameraControls
            camera={controlsRef.current?.object}
            controls={controlsRef.current}
            model={modelRef.current}
            isVisible={showCameraControls}
            onToggle={() => setShowCameraControls(false)}
          />
        )}
      </Box>

      {modelInfo && !loading && !error && (
        <HStack spacing={2} wrap="wrap" justify="center">
          <Badge variant="subtle" bg="ancient.terracotta" color="white">
            {modelInfo.vertices.toLocaleString()} vertices
          </Badge>
          <Badge variant="subtle" bg="ancient.gold" color="white">
            {modelInfo.faces.toLocaleString()} faces
          </Badge>
          <Badge variant="subtle" bg="ancient.stone" color="white">
            {getFileExtension().toUpperCase()}
          </Badge>
        </HStack>
      )}
    </VStack>
  );
};

export default ThreeDViewer;
